/**
 * WizardStepper Component — Xyden Leadership Assessment Platform
 * Step 6F Green Phase
 *
 * Horizontal step indicator with connected lines between numbered circles.
 */

import React from "react";
import { Check } from "lucide-react";

export interface WizardStepperProps {
  steps: readonly string[];
  currentStep: number;
  completedSteps?: number[];
}

export function WizardStepper({ steps, currentStep, completedSteps }: WizardStepperProps) {
  const completed = completedSteps ?? steps.map((_, i) => i).filter((i) => i < currentStep);

  return (
    <nav aria-label="Wizard steps">
      <ol role="list" className="flex items-center px-8 w-full">
        {steps.map((step, index) => {
          const isCompleted = completed.includes(index);
          const isCurrent = index === currentStep;
          const isLast = index === steps.length - 1;

          return (
            <li
              key={step}
              role="listitem"
              aria-current={isCurrent ? "step" : undefined}
              className={`flex items-center ${isLast ? "" : "flex-1"}`}
              title={step}
            >
              {/* Circle + label */}
              <div className="flex items-center gap-2 shrink-0">
                <div
                  className={`flex items-center justify-center w-8 h-8 rounded-full text-xs font-semibold transition-colors ${
                    isCurrent
                      ? "bg-brand-crimson text-white"
                      : isCompleted
                      ? "bg-green-600 text-white"
                      : "bg-brand-steel/20 text-brand-steel"
                  }`}
                >
                  {isCompleted ? (
                    <Check size={14} aria-label={`${step} completed`} />
                  ) : (
                    <span aria-hidden="true">{index + 1}</span>
                  )}
                </div>
                <span
                  className={`text-sm whitespace-nowrap ${
                    isCurrent
                      ? "text-gray-900 font-medium"
                      : isCompleted
                      ? "text-green-600 font-medium"
                      : "text-brand-steel"
                  }`}
                >
                  {step}
                </span>
              </div>

              {/* Connecting line */}
              {!isLast && (
                <div
                  className={`flex-1 h-0.5 mx-4 ${isCompleted ? "bg-green-600" : "bg-brand-steel/30"}`}
                  aria-hidden="true"
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export default WizardStepper;
